import React, { useEffect, useRef, useState } from "react";
import { SettingsSearchProvider } from "./SettingsSearchContext";
import SettingsSearchBar from "./SettingsSearchBar";
import SettingsSidebar from "./SettingsSidebar";
import SettingsSearchSync from "./SettingsSearchSync";
import {
  findNavItem,
  defaultSectionId,
} from "../data/settingsNavConfig";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  readSettingsSection,
  writeSettingsSection,
  bindScrollMemory,
} from "../utils/pageMemory";
import WelcomeSection from "./settings/WelcomeSection";
import LeaveSection from "./settings/LeaveSection";
import RolesSection from "./settings/RolesSection";
import SpiritCounterSection from "./settings/SpiritCounterSection";
import GuardianSection from "./settings/GuardianSection";
import AutoModSection from "./settings/AutoModSection";
import ChannelsSection from "./settings/ChannelsSection";
import LevelingSection from "./settings/LevelingSection";
import ServerLevelingSection from "./settings/ServerLevelingSection";
import BoostingSection from "./settings/BoostingSection";
import DonorSupportSection from "./settings/DonorSupportSection";
import {
  DEFAULT_DONOR_ANNOUNCE,
  resolveDonorAnnounceMessage,
} from "../data/donorDefaults";
import StreamAlertsSection from "./settings/StreamAlertsSection";
import QotdSection from "./settings/QotdSection";
import ConfessionsSection from "./settings/ConfessionsSection";
import ShopSection from "./settings/ShopSection";
import PartnershipSection from "./settings/PartnershipSection";
import RolePanelsSection from "./settings/RolePanelsSection";
import ColorPanelsSection from "./settings/ColorPanelsSection";
import StaffQuotaSection from "./settings/StaffQuotaSection";
import TicketsSection from "./settings/TicketsSection";
import StaffTasksSection from "./settings/StaffTasksSection";
import GiveawaySection from "./settings/GiveawaySection";
import ApplicationsSection from "./settings/ApplicationsSection";
import KamiProfileSection from "./settings/KamiProfileSection";
import HostSection from "./settings/HostSection";
import {
  DEFAULT_APPLICATION_FIELDS,
  DEFAULT_APPLICATION_FORM_TITLE,
  DEFAULT_APPLICATION_MESSAGE,
} from "../data/applicationDefaults";
import { DEFAULT_GIVEAWAY_STYLE } from "../data/giveawayDefaults";
import VerificationSection from "./settings/VerificationSection";
import DiscordEmbedPreview from "./DiscordEmbedPreview";
import { getDiscordAvatarUrl } from "../utils/discord";
import { formatDiscordPreviewHtml } from "../utils/discordPreview";
import { isAiGuild, isHomeGuild } from "../data/aiGuilds";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function SettingsPage({ guilds, user }) {
  const { guildId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardToken =
    typeof window !== "undefined" ? window.localStorage.getItem("kami_token") : null;
  const showAiChannel = isAiGuild(guildId);
  const showProgressionAdmin = isHomeGuild(guildId);
  const [channels, setChannels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeSection, setActiveSection] = useState(() => {
    const remembered = readSettingsSection(guildId);
    if (remembered && findNavItem(remembered, isHomeGuild(guildId))) {
      return remembered;
    }
    return defaultSectionId(isHomeGuild(guildId));
  });
  const [isMobileSettings, setIsMobileSettings] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 900px)").matches
      : false,
  );
  const [mobileShowIndex, setMobileShowIndex] = useState(() => {
    if (typeof window === "undefined") return false;
    const mobile = window.matchMedia("(max-width: 900px)").matches;
    if (!mobile) return false;
    // If we restored a section from URL/storage, open that section (not the index).
    const remembered = readSettingsSection(guildId);
    return !(remembered && findNavItem(remembered, isHomeGuild(guildId)));
  });
  const settingsFormRef = useRef(null);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // Welcome State
  const [welcomeMsg, setWelcomeMsg] = useState("");
  const [selectedChannel, setSelectedChannel] = useState("");
  const [welcomeTitle, setWelcomeTitle] = useState("☾ Nocturne");
  const [welcomeFooter, setWelcomeFooter] = useState("");
  const [embedColor, setEmbedColor] = useState("#4a0e17");
  const [showThumbnail, setShowThumbnail] = useState(true);
  const [showTimestamp, setShowTimestamp] = useState(true);
  const [welcomeMention, setWelcomeMention] = useState(true);
  const [welcomeImage, setWelcomeImage] = useState("");
  const [welcomePingEnabled, setWelcomePingEnabled] = useState(false);
  const [welcomePingChannelId, setWelcomePingChannelId] = useState("");
  const [welcomePingRoleId, setWelcomePingRoleId] = useState("");
  const [welcomePingMessage, setWelcomePingMessage] = useState("");
  const [leaveImage, setLeaveImage] = useState("");
  const [embedImageUploading, setEmbedImageUploading] = useState(false);
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(false);

  // Leave State
  const [leaveMsg, setLeaveMsg] = useState("");
  const [selectedLeaveChannel, setSelectedLeaveChannel] = useState("");
  const [leaveTitle, setLeaveTitle] = useState("✦ Farewell");
  const [leaveFooter, setLeaveFooter] = useState("");
  const [leaveColor, setLeaveColor] = useState("#4a0e17");
  const [showLeaveThumbnail, setShowLeaveThumbnail] = useState(false);
  const [showLeaveTimestamp, setShowLeaveTimestamp] = useState(true);
  const [logLeaves, setLogLeaves] = useState(false);
  const [isLeaveOpen, setIsLeaveOpen] = useState(false);

  // Roles & channels
  const [memberRole, setMemberRole] = useState([]);
  const [unverifiedRole, setUnverifiedRole] = useState("");
  const [jailRole, setJailRole] = useState("");
  const [autoRoleId, setAutoRoleId] = useState("");
  const [aiChannel, setAiChannel] = useState("");
  const [reportLogChannel, setReportLogChannel] = useState("");
  const [jailChannel, setJailChannel] = useState("");
  const [suggestChannel, setSuggestChannel] = useState("");
  const [botAnnouncementsChannel, setBotAnnouncementsChannel] = useState("");
  const [birthdayChannel, setBirthdayChannel] = useState("");
  const [countingChannel, setCountingChannel] = useState("");
  const [inviteLogChannel, setInviteLogChannel] = useState("");
  const [quotesChannelId, setQuotesChannelId] = useState("");
  const [commandPrefix, setCommandPrefix] = useState("");
  const [commandPrefixDirty, setCommandPrefixDirty] = useState(false);

  // Support tickets
  const [ticketPanelChannelId, setTicketPanelChannelId] = useState("");
  const [ticketCategoryId, setTicketCategoryId] = useState("");
  const [ticketStaffRoleId, setTicketStaffRoleId] = useState("");
  const [ticketPanelTitle, setTicketPanelTitle] = useState("☾ Support Desk");
  const [ticketPanelDescription, setTicketPanelDescription] = useState(
    "Need help? Open a private channel and a Warden will meet you here.\n\nClick the button below to start a support ticket.",
  );
  const [ticketPanelFooter, setTicketPanelFooter] = useState(
    "Nocturne | Support Desk",
  );
  const [ticketButtonLabel, setTicketButtonLabel] = useState("Open Support");
  const [ticketWelcomeTitle, setTicketWelcomeTitle] =
    useState("✦ Support Request");
  const [ticketWelcomeDescription, setTicketWelcomeDescription] = useState(
    "Welcome, {user}. A Warden will be with you shortly.",
  );
  const [ticketPingStaffOnOpen, setTicketPingStaffOnOpen] = useState(false);
  const [ticketPingRoleId, setTicketPingRoleId] = useState("");
  const [ticketCloseRoleId, setTicketCloseRoleId] = useState("");
  const [ticketTranscriptChannelId, setTicketTranscriptChannelId] =
    useState("");
  const [isTicketsOpen, setIsTicketsOpen] = useState(false);
  const [staffTasks, setStaffTasks] = useState({
    channelId: "",
    creatorRoleIds: [],
  });
  const [isStaffTasksOpen, setIsStaffTasksOpen] = useState(false);
  const [giveawayStyle, setGiveawayStyle] = useState({
    ...DEFAULT_GIVEAWAY_STYLE,
  });
  const [isGiveawayOpen, setIsGiveawayOpen] = useState(false);
  const [isApplicationsOpen, setIsApplicationsOpen] = useState(false);
  const [verificationTitle, setVerificationTitle] = useState(
    "Nocturne Verification",
  );
  const [verificationDescription, setVerificationDescription] = useState(
    "By clicking the button below, you agree to the server rules and gain access to the rest of Nocturne.",
  );
  const [verificationFooter, setVerificationFooter] = useState(
    "Nocturne | Verification",
  );
  const [verificationButtonLabel, setVerificationButtonLabel] = useState(
    "Complete Verification",
  );
  const [verificationMode, setVerificationMode] = useState("button");
  const [isVerificationOpen, setIsVerificationOpen] = useState(false);

  // Leveling & shop
  const [levelingEnabled, setLevelingEnabled] = useState(true);
  const [levelUpChannel, setLevelUpChannel] = useState("");
  const [levelRoles, setLevelRoles] = useState([]);
  const [serverLevelingEnabled, setServerLevelingEnabled] = useState(false);
  const [serverLevelUpChannel, setServerLevelUpChannel] = useState("");
  const [globalMultiplier, setGlobalMultiplier] = useState(1);
  const [multiplierExpires, setMultiplierExpires] = useState(null);
  const [boosterHours, setBoosterHours] = useState(0);
  const [customBoosterRoles, setCustomBoosterRoles] = useState(false);
  const [siteSupporterPerksEnabled, setSiteSupporterPerksEnabled] =
    useState(true);
  const [boostRoleId, setBoostRoleId] = useState("");
  const [boostChannel, setBoostChannel] = useState("");
  const [boostMessage, setBoostMessage] = useState(
    "☾ {user} just boosted **{server}**.\nThe realm now holds **{server.boosts}** boosts.",
  );
  const [customRoleAnchorId, setCustomRoleAnchorId] = useState("");
  const [customRolePlacement, setCustomRolePlacement] = useState("above");
  const [donorSupport, setDonorSupport] = useState({
    roleIds: { supporter: "", patron: "", benefactor: "" },
    announceChannelId: "",
    announceMessage: DEFAULT_DONOR_ANNOUNCE,
  });
  const [streamAlerts, setStreamAlerts] = useState({
    enabled: false,
    channelId: "",
    pingRoleId: "",
    mentionEveryone: false,
    combineMultiPlatform: false,
    message: "🔴 **{streamer}** is live on **{platform}**!\n{title}\n{url}",
    creators: [],
  });
  const [qotd, setQotd] = useState({
    enabled: false,
    channelId: "",
    pingRoleId: "",
    timezone: "America/Chicago",
    postHour: 9,
    title: "❓ Question of the Day",
    source: "both",
    questions: [],
  });
  const [confessions, setConfessions] = useState({
    enabled: false,
    channelId: "",
    panelChannelId: "",
    logChannelId: "",
    embedColor: "#4a0e17",
    panelTitle: "Anonymous Confessions",
    panelDescription:
      "Share something anonymously with the server.\n\nUse the buttons below to submit a confession, start a poll, or reply to an existing confession.",
    panelFooter: "Kami · Confessions",
    allowPolls: true,
    allowReplies: true,
    allowCustomColor: true,
    cooldownSeconds: 60,
  });
  const [shopItems, setShopItems] = useState([]);

  // Partnerships & role panels
  const [applications, setApplications] = useState({
    enabled: false,
    formTitle: DEFAULT_APPLICATION_FORM_TITLE,
    message: DEFAULT_APPLICATION_MESSAGE,
    fields: DEFAULT_APPLICATION_FIELDS.map((field) => ({ ...field })),
    logChannelId: "",
    supportChannelId: "",
    acceptRoleIds: [],
  });
  const [partnershipConfig, setPartnershipConfig] = useState({
    channelId: "",
    logChannelId: "",
    partnerRole: "",
    pingRoleId: "",
    embedColor: "#4a0e17",
    defaultAd: "",
    cooldownHours: 24,
  });
  const [partnershipBlacklist, setPartnershipBlacklist] = useState([]);
  const [newBlacklistId, setNewBlacklistId] = useState("");
  const [rolePanels, setRolePanels] = useState([]);
  const [colorPanels, setColorPanels] = useState({
    panelTitle: "",
    panelMessage: "",
  });
  const [staffQuota, setStaffQuota] = useState({
    enabled: false,
    staffRoleIds: [],
    messageGoal: 50,
    voiceMinutesGoal: 60,
    minMessageLength: 1,
    periodDays: 7,
    reportChannelId: "",
    ignoreAfk: true,
  });

  // Growth & Guardian State
  const [forbiddenWords, setForbiddenWords] = useState([]);
  const [modLogChannel, setModLogChannel] = useState("");
  const [banDmEnabled, setBanDmEnabled] = useState(true);
  const [banAppealsEnabled, setBanAppealsEnabled] = useState(false);
  const [banAppealChannelId, setBanAppealChannelId] = useState("");
  const [banUnbanServerEnabled, setBanUnbanServerEnabled] = useState(false);
  const [banUnbanServerInvite, setBanUnbanServerInvite] = useState("");
  const [banUnbanServerMessage, setBanUnbanServerMessage] = useState("");
  const [messageLogChannel, setMessageLogChannel] = useState("");
  const [logs, setLogs] = useState({
    mod: { channelId: "", enabled: true },
    admin: { channelId: "", enabled: true },
    message: {
      channelId: "",
      enabled: true,
      sensitiveChannelId: "",
      sensitiveSourceChannelIds: [],
    },
    member: { channelId: "", enabled: true },
    joinLeave: { channelId: "", enabled: true },
    automod: { channelId: "", enabled: true },
    voice: { channelId: "", enabled: false },
    server: { channelId: "", enabled: false },
    report: { channelId: "", enabled: true },
    actions: {},
  });
  const [antiLinks, setAntiLinks] = useState(false);
  const [linkBlacklist, setLinkBlacklist] = useState([]);
  const [antiLinkExemptChannelIds, setAntiLinkExemptChannelIds] = useState([]);
  const [antiLinkExemptCategoryIds, setAntiLinkExemptCategoryIds] = useState(
    [],
  );
  const [antiSpamEnabled, setAntiSpamEnabled] = useState(false);
  const [antiSpamMaxMessages, setAntiSpamMaxMessages] = useState(5);
  const [antiSpamIntervalSeconds, setAntiSpamIntervalSeconds] = useState(5);
  const [antiSpamExemptChannelIds, setAntiSpamExemptChannelIds] = useState([]);
  const [antiSpamExemptCategoryIds, setAntiSpamExemptCategoryIds] = useState(
    [],
  );
  const [maxMentions, setMaxMentions] = useState(0);
  const [growthChannel, setGrowthChannel] = useState("");
  const [growthMessage, setGrowthMessage] = useState("☾ Members: {count}");
  const [serverStats, setServerStats] = useState([]);
  const [statCounts, setStatCounts] = useState({});
  const [isGrowthOpen, setGrowthOpen] = useState(false);

  // Moderator Command Toggles State
  const [commandToggles, setCommandToggles] = useState({
    enabled: true,
    commands: {
      ban: true,
      kick: true,
      warn: true,
      purge: true,
      purify: true,
      history: true,
      clearhistory: true,
      jail: true,
      unjail: true,
      timeout: true,
      unban: true,
    },
    autoModEnabled: true,
  });

  const [security, setSecurity] = useState({
    enabled: false,
    action: "jail",
    minAccountAgeDays: 7,
    blockDefaultAvatar: true,
    suspiciousNames: true,
    joinFloodCount: 5,
    joinFloodSeconds: 10,
    scoreThreshold: 2,
  });
  const [falseGate, setFalseGate] = useState({
    enabled: false,
    channelId: "",
    action: "ban",
    timeoutMinutes: 60,
    deleteMessage: true,
    ignoreStaff: true,
    reason: "Spoke in the False Gate.",
    caughtCount: 0,
    panelMessageId: null,
  });

  // Temp UI States
  const [newWord, setNewWord] = useState("");
  const [newLink, setNewLink] = useState("");
  const [stats, setStats] = useState({
    memberCount: "...",
    ownerName: "Loading...",
  });

  const showNotification = (msg, type = "success") => {
    setToast({ show: true, message: msg, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      3000,
    );
  };

  const guild = guilds.find((g) => g.id === guildId);
  const userAvatar = getDiscordAvatarUrl(user);
  const hasPreview = [
    "welcome",
    "leave",
    "verification",
    "serverStats",
    "tickets",
    "giveaways",
  ].includes(activeSection);

  const activeNavItem = findNavItem(activeSection, showProgressionAdmin);

  useEffect(() => {
    if (!findNavItem(activeSection, showProgressionAdmin)) {
      setActiveSection(defaultSectionId(showProgressionAdmin));
    }
  }, [showProgressionAdmin, activeSection]);

  // Keep URL ?section= + localStorage in sync so refresh returns here.
  useEffect(() => {
    if (!guildId || !activeSection) return;
    if (!findNavItem(activeSection, showProgressionAdmin)) return;
    writeSettingsSection(guildId, activeSection);
    if (searchParams.get("section") !== activeSection) {
      const next = new URLSearchParams(searchParams);
      next.set("section", activeSection);
      setSearchParams(next, { replace: true });
    }
  }, [activeSection, guildId, showProgressionAdmin, searchParams, setSearchParams]);

  // Switching servers: restore that guild's last section.
  useEffect(() => {
    const remembered = readSettingsSection(guildId);
    if (remembered && findNavItem(remembered, showProgressionAdmin)) {
      setActiveSection(remembered);
      return;
    }
    setActiveSection(defaultSectionId(showProgressionAdmin));
  }, [guildId, showProgressionAdmin]);

  // Restore scroll for this guild + section after refresh.
  useEffect(() => {
    if (!guildId || !activeSection || mobileShowIndex) return undefined;
    return bindScrollMemory(`manage/${guildId}/${activeSection}`);
  }, [guildId, activeSection, mobileShowIndex]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const sync = () => {
      const mobile = mq.matches;
      setIsMobileSettings(mobile);
      if (!mobile) setMobileShowIndex(false);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const handleSelectSection = (sectionId) => {
    setActiveSection(sectionId);
    if (isMobileSettings) {
      setMobileShowIndex(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleMobileBackToIndex = () => {
    setMobileShowIndex(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ChannelPicker/RolePicker mark dirty via this event (React onChange ignores non-input targets).
  useEffect(() => {
    const form = settingsFormRef.current;
    if (!form) return undefined;
    const onDirty = () => {
      if (!isSaving) setHasUnsavedChanges(true);
    };
    form.addEventListener("settings-dirty", onDirty);
    return () => form.removeEventListener("settings-dirty", onDirty);
  }, [isSaving, guildId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [chanRes, setRes, roleRes, statRes] = await Promise.all([
          fetch(`${API_BASE}/channels/${guildId}`),
          fetch(`${API_BASE}/settings/${guildId}`),
          fetch(`${API_BASE}/roles/${guildId}`),
          fetch(`${API_BASE}/guild-stats/${guildId}`),
        ]);

        const chanData = await chanRes.json();
        setChannels(Array.isArray(chanData) ? chanData : []);

        const settings = await setRes.json();
        if (settings && Object.keys(settings).length > 0) {
          setWelcomeMsg(settings.welcomeMessage || "");
          setSelectedChannel(settings.welcomeChannel || "");
          setEmbedColor(settings.embedColor || "#4a0e17");
          setWelcomeTitle(settings.welcomeTitle || "☾ Nocturne");
          setShowThumbnail(settings.showThumbnail ?? true);
          setShowTimestamp(settings.showTimestamp ?? true);
          setWelcomeMention(settings.welcomeMention ?? true);
          setWelcomeImage(settings.welcomeImage || "");
          setWelcomePingEnabled(Boolean(settings.welcomePingEnabled));
          setWelcomePingChannelId(settings.welcomePingChannelId || "");
          setWelcomePingRoleId(settings.welcomePingRoleId || "");
          setWelcomePingMessage(settings.welcomePingMessage || "");

          setMemberRole(
            Array.isArray(settings.memberRole) ? settings.memberRole : [],
          );
          setUnverifiedRole(settings.unverifiedRole || "");
          setJailRole(settings.jailRole || "");
          setAutoRoleId(settings.autoRoleId || "");
          setAiChannel(settings.aiChannel || "");
          setReportLogChannel(settings.reportLogChannel || "");
          setJailChannel(settings.jailChannel || "");
          setSuggestChannel(settings.suggestChannel || "");
          setBotAnnouncementsChannel(settings.botAnnouncementsChannel || "");
          setBirthdayChannel(settings.birthdayChannel || "");
          setCountingChannel(settings.counting?.channelId || "");
          setInviteLogChannel(settings.inviteLogChannel || "");
          setQuotesChannelId(settings.quotesChannelId || "");
          setCommandPrefix(settings.commandPrefix || "");
          setCommandPrefixDirty(false);

          const t = settings.tickets || {};
          setTicketPanelChannelId(t.panelChannelId || "");
          setTicketCategoryId(t.categoryId || "");
          setTicketStaffRoleId(t.staffRoleId || "");
          setTicketPanelTitle(t.panelTitle || "☾ Support Desk");
          setTicketPanelDescription(
            t.panelDescription ||
              "Need help? Open a private channel and a Warden will meet you here.\n\nClick the button below to start a support ticket.",
          );
          setTicketPanelFooter(t.panelFooter || "Nocturne | Support Desk");
          setTicketButtonLabel(t.buttonLabel || "Open Support");
          setTicketWelcomeTitle(t.welcomeTitle || "✦ Support Request");
          setTicketWelcomeDescription(
            t.welcomeDescription ||
              "Welcome, {user}. A Warden will be with you shortly.",
          );
          setTicketPingStaffOnOpen(Boolean(t.pingStaffOnOpen));
          setTicketPingRoleId(t.pingRoleId || "");
          setTicketCloseRoleId(t.closeRoleId || "");
          setTicketTranscriptChannelId(t.transcriptChannelId || "");

          const st = settings.staffTasks || {};
          setStaffTasks({
            channelId: st.channelId || "",
            creatorRoleIds: Array.isArray(st.creatorRoleIds)
              ? st.creatorRoleIds
              : [],
          });

          const gs = settings.giveawayStyle || {};
          setGiveawayStyle({ ...DEFAULT_GIVEAWAY_STYLE, ...gs });

          const apps = settings.applications || {};
          setApplications({
            enabled: Boolean(apps.enabled),
            formTitle: apps.formTitle || DEFAULT_APPLICATION_FORM_TITLE,
            message: apps.message || DEFAULT_APPLICATION_MESSAGE,
            fields:
              apps.fields?.length > 0
                ? apps.fields
                : DEFAULT_APPLICATION_FIELDS.map((field) => ({ ...field })),
            logChannelId: apps.logChannelId || "",
            supportChannelId: apps.supportChannelId || "",
            acceptRoleIds: Array.isArray(apps.acceptRoleIds)
              ? apps.acceptRoleIds
              : [],
          });

          const v = settings.verification || {};
          setVerificationMode(v.mode === "oauth" ? "oauth" : "button");
          setVerificationTitle(v.title || "Nocturne Verification");
          setVerificationDescription(
            v.description ||
              "By clicking the button below, you agree to the server rules and gain access to the rest of Nocturne.",
          );
          setVerificationFooter(v.footer || "Nocturne | Verification");
          setVerificationButtonLabel(
            v.buttonLabel || "Complete Verification",
          );

          setLeaveMsg(settings.leaveMessage || "");
          setSelectedLeaveChannel(settings.leaveChannel || "");
          setLeaveTitle(settings.leaveTitle || "✦ Farewell");
          setLeaveColor(settings.leaveColor || "#4a0e17");
          setShowLeaveThumbnail(settings.showLeaveThumbnail ?? false);
          setShowLeaveTimestamp(settings.showLeaveTimestamp ?? true);
          setLeaveImage(settings.leaveImage || "");
          setLogLeaves(settings.logLeaves ?? false);
          setForbiddenWords(settings.forbiddenWords || []);
          setBanDmEnabled(settings.banDmEnabled !== false);
          setBanAppealsEnabled(Boolean(settings.banAppealsEnabled));
          setBanAppealChannelId(settings.banAppealChannelId || "");
          setBanUnbanServerEnabled(Boolean(settings.banUnbanServerEnabled));
          setBanUnbanServerInvite(settings.banUnbanServerInvite || "");
          setBanUnbanServerMessage(settings.banUnbanServerMessage || "");
          const loadedLogs = settings.logs || {};
          const nextLogs = {
            mod: {
              channelId:
                loadedLogs.mod?.channelId || settings.modLogChannel || "",
              enabled: loadedLogs.mod?.enabled !== false,
            },
            admin: {
              channelId: loadedLogs.admin?.channelId || "",
              enabled: loadedLogs.admin?.enabled !== false,
            },
            message: {
              channelId:
                loadedLogs.message?.channelId || settings.messageLogChannel || "",
              enabled: loadedLogs.message?.enabled !== false,
              sensitiveChannelId:
                loadedLogs.message?.sensitiveChannelId || "",
              sensitiveSourceChannelIds: Array.isArray(
                loadedLogs.message?.sensitiveSourceChannelIds,
              )
                ? loadedLogs.message.sensitiveSourceChannelIds
                : [],
            },
            member: {
              channelId: loadedLogs.member?.channelId || "",
              enabled: loadedLogs.member?.enabled !== false,
            },
            joinLeave: {
              channelId: loadedLogs.joinLeave?.channelId || "",
              enabled: loadedLogs.joinLeave?.enabled !== false,
            },
            automod: {
              channelId: loadedLogs.automod?.channelId || "",
              enabled: loadedLogs.automod?.enabled !== false,
            },
            voice: {
              channelId: loadedLogs.voice?.channelId || "",
              enabled: loadedLogs.voice?.enabled === true,
            },
            server: {
              channelId: loadedLogs.server?.channelId || "",
              enabled: loadedLogs.server?.enabled === true,
            },
            report: {
              channelId:
                loadedLogs.report?.channelId || settings.reportLogChannel || "",
              enabled: loadedLogs.report?.enabled !== false,
            },
            actions:
              loadedLogs.actions && typeof loadedLogs.actions === "object"
                ? loadedLogs.actions
                : {},
          };
          setLogs(nextLogs);
          setModLogChannel(nextLogs.mod.channelId);
          setMessageLogChannel(nextLogs.message.channelId);
          setReportLogChannel(nextLogs.report.channelId);
          setWelcomeFooter(settings.welcomeFooter || "");
          setLeaveFooter(settings.leaveFooter || "");
          setAntiLinks(settings.antiLinks ?? false);
          setLinkBlacklist(settings.linkBlacklist || []);
          setAntiLinkExemptChannelIds(
            settings.antiLinkExemptChannelIds || [],
          );
          setAntiLinkExemptCategoryIds(
            settings.antiLinkExemptCategoryIds || [],
          );
          setAntiSpamEnabled(settings.antiSpamEnabled ?? false);
          setAntiSpamMaxMessages(settings.antiSpamMaxMessages || 5);
          setAntiSpamIntervalSeconds(settings.antiSpamIntervalSeconds || 5);
          setAntiSpamExemptChannelIds(
            settings.antiSpamExemptChannelIds || [],
          );
          setAntiSpamExemptCategoryIds(
            settings.antiSpamExemptCategoryIds || [],
          );
          setMaxMentions(settings.maxMentions || 0);
          setGrowthChannel(settings.growthChannel || "");
          setGrowthMessage(settings.growthMessage || "☾ Members: {count}");
          if (Array.isArray(settings.serverStats) && settings.serverStats.length) {
            setServerStats(settings.serverStats);
          } else if (settings.growthChannel) {
            setServerStats([
              {
                id: "legacy_growth",
                channelId: settings.growthChannel,
                type: "all",
                roleId: "",
                format: settings.growthMessage || "☾ Members: {count}",
              },
            ]);
          } else {
            setServerStats([]);
          }

          setLevelingEnabled(settings.levelingEnabled ?? true);
          setLevelUpChannel(settings.levelUpChannel || "");
          setServerLevelingEnabled(Boolean(settings.serverLevelingEnabled));
          setServerLevelUpChannel(settings.serverLevelUpChannel || "");
          setLevelRoles(
            Array.isArray(settings.levelRoles)
              ? settings.levelRoles.map((entry) => ({
                  level: Number(entry.level) || 1,
                  roleId: String(entry.roleId || ""),
                }))
              : [],
          );
          setGlobalMultiplier(settings.globalMultiplier ?? 1);
          setMultiplierExpires(settings.multiplierExpires || null);
          setCustomBoosterRoles(settings.boosting?.customRolesEnabled ?? false);
          setSiteSupporterPerksEnabled(
            settings.siteSupporterPerksEnabled !== false,
          );
          setBoostRoleId(settings.boosting?.boostRoleId || "");
          setBoostChannel(settings.boosting?.boostChannel || "");
          setBoostMessage(
            settings.boosting?.boostMessage ||
              "☾ {user} just boosted **{server}**.\nThe realm now holds **{server.boosts}** boosts.",
          );
          setCustomRoleAnchorId(settings.boosting?.customRoleAnchorId || "");
          setCustomRolePlacement(
            settings.boosting?.customRolePlacement === "below"
              ? "below"
              : "above",
          );
          setDonorSupport({
            roleIds: {
              supporter: settings.donorSupport?.roleIds?.supporter || "",
              patron: settings.donorSupport?.roleIds?.patron || "",
              benefactor: settings.donorSupport?.roleIds?.benefactor || "",
            },
            announceChannelId: settings.donorSupport?.announceChannelId || "",
            announceMessage: resolveDonorAnnounceMessage(
              settings.donorSupport?.announceMessage,
            ),
          });
          setStreamAlerts({
            enabled: settings.streamAlerts?.enabled ?? false,
            channelId: settings.streamAlerts?.channelId || "",
            pingRoleId: settings.streamAlerts?.pingRoleId || "",
            mentionEveryone: settings.streamAlerts?.mentionEveryone ?? false,
            combineMultiPlatform:
              settings.streamAlerts?.combineMultiPlatform ?? false,
            message:
              settings.streamAlerts?.message ||
              "🔴 **{streamer}** is live on **{platform}**!\n{title}\n{url}",
            creators: (settings.streamAlerts?.creators || []).map((creator) => ({
              id:
                creator.id ||
                creator._id?.toString?.() ||
                crypto.randomUUID(),
              platform: creator.platform || "twitch",
              platformId: creator.platformId || "",
              displayName: creator.displayName || "",
              discordUserId: creator.discordUserId || "",
            })),
          });
          setQotd({
            enabled: settings.qotd?.enabled ?? false,
            channelId: settings.qotd?.channelId || "",
            pingRoleId: settings.qotd?.pingRoleId || "",
            timezone: settings.qotd?.timezone || "America/Chicago",
            postHour: settings.qotd?.postHour ?? 9,
            title: settings.qotd?.title || "❓ Question of the Day",
            source: settings.qotd?.source || "both",
            questions: Array.isArray(settings.qotd?.questions)
              ? settings.qotd.questions.filter(Boolean)
              : [],
          });
          setConfessions({
            enabled: settings.confessions?.enabled ?? false,
            channelId: settings.confessions?.channelId || "",
            panelChannelId: settings.confessions?.panelChannelId || "",
            logChannelId: settings.confessions?.logChannelId || "",
            embedColor: settings.confessions?.embedColor || "#4a0e17",
            panelTitle:
              settings.confessions?.panelTitle || "Anonymous Confessions",
            panelDescription:
              settings.confessions?.panelDescription ||
              "Share something anonymously with the server.\n\nUse the buttons below to submit a confession, start a poll, or reply to an existing confession.",
            panelFooter:
              settings.confessions?.panelFooter || "Kami · Confessions",
            allowPolls: settings.confessions?.allowPolls !== false,
            allowReplies: settings.confessions?.allowReplies !== false,
            allowCustomColor: settings.confessions?.allowCustomColor !== false,
            cooldownSeconds: settings.confessions?.cooldownSeconds ?? 60,
          });
          setShopItems(settings.shopItems || []);
          setRolePanels(settings.rolePanels || []);
          setColorPanels({
            panelTitle: settings.colorPanels?.panelTitle || "",
            panelMessage: settings.colorPanels?.panelMessage || "",
          });
          setPartnershipConfig({
            channelId: settings.partnershipConfig?.channelId || "",
            logChannelId: settings.partnershipConfig?.logChannelId || "",
            partnerRole: settings.partnershipConfig?.partnerRole || "",
            pingRoleId: settings.partnershipConfig?.pingRoleId || "",
            embedColor: settings.partnershipConfig?.embedColor || "#4a0e17",
            defaultAd: settings.partnershipConfig?.defaultAd || "",
            cooldownHours: settings.partnershipConfig?.cooldownHours ?? 24,
          });
          setPartnershipBlacklist(settings.partnershipBlacklist || []);
          setStaffQuota({
            enabled: settings.staffQuota?.enabled ?? false,
            staffRoleIds: settings.staffQuota?.staffRoleIds || [],
            messageGoal: settings.staffQuota?.messageGoal ?? 50,
            voiceMinutesGoal: settings.staffQuota?.voiceMinutesGoal ?? 60,
            minMessageLength: settings.staffQuota?.minMessageLength ?? 1,
            periodDays: settings.staffQuota?.periodDays ?? 7,
            reportChannelId: settings.staffQuota?.reportChannelId || "",
            ignoreAfk: settings.staffQuota?.ignoreAfk ?? true,
          });

          setSecurity({
            enabled: settings.security?.enabled ?? false,
            action: ["jail", "kick", "log"].includes(settings.security?.action)
              ? settings.security.action
              : "jail",
            minAccountAgeDays: settings.security?.minAccountAgeDays ?? 7,
            blockDefaultAvatar: settings.security?.blockDefaultAvatar !== false,
            suspiciousNames: settings.security?.suspiciousNames !== false,
            joinFloodCount: settings.security?.joinFloodCount ?? 5,
            joinFloodSeconds: settings.security?.joinFloodSeconds ?? 10,
            scoreThreshold: settings.security?.scoreThreshold ?? 2,
          });
          setFalseGate({
            enabled: Boolean(settings.falseGate?.enabled),
            channelId: settings.falseGate?.channelId || "",
            action: [
              "ban",
              "softban",
              "kick",
              "jail",
              "timeout",
              "log",
            ].includes(settings.falseGate?.action)
              ? settings.falseGate.action
              : "ban",
            timeoutMinutes: settings.falseGate?.timeoutMinutes ?? 60,
            deleteMessage: settings.falseGate?.deleteMessage !== false,
            ignoreStaff: settings.falseGate?.ignoreStaff !== false,
            reason:
              settings.falseGate?.reason || "Spoke in the False Gate.",
            caughtCount: Number(settings.falseGate?.caughtCount) || 0,
            panelMessageId: settings.falseGate?.panelMessageId || null,
          });

          if (settings.commandToggles) {
            setCommandToggles({
              enabled: settings.commandToggles.enabled ?? true,
              autoModEnabled: settings.commandToggles.autoModEnabled ?? true,
              commands: {
                ban: true,
                kick: true,
                warn: true,
                purge: true,
      purify: true,
                history: true,
                clearhistory: true,
                jail: true,
                unjail: true,
                timeout: true,
                unban: true,
                ...settings.commandToggles.commands,
              },
            });
          }
        }

        const roleData = await roleRes.json();
        setRoles(Array.isArray(roleData) ? roleData : []);

        const statData = await statRes.json();
        setStats({
          memberCount: statData.memberCount,
          ownerName: statData.ownerName,
        });
      } catch (err) {
        console.error("Initialization error:", err);
      }
    };
    fetchData();
  }, [guildId]);

  const handleToggleCommand = (cmd) => {
    setCommandToggles((prev) => ({
      ...prev,
      commands: {
        ...prev.commands,
        [cmd]: !prev.commands[cmd],
      },
    }));
  };

  const formatPreviewText = (text) =>
    formatDiscordPreviewHtml(text, {
      userName: user?.username || "New Member",
      serverName: guild?.name || "Nocturne",
      count: String(stats.memberCount || "0"),
      prize: "Discord Nitro",
      host: user?.username || "Nexy",
      winners: "1",
      winnerList: user?.username || "Nexy",
      ends: "in 2 days",
      emoji: giveawayStyle.emoji || "🎉",
      requirements: "• Level 5+\n• 50+ messages (last 7 days)",
    });

  const fillGiveawayPlain = (text) =>
    String(text || "")
      .replace(/\{prize\}/gi, "Discord Nitro")
      .replace(/\{host\}/gi, user?.username || "Nexy")
      .replace(/\{winners\}/gi, "1")
      .replace(/\{winnerList\}/gi, user?.username || "Nexy")
      .replace(/\{ends\}/gi, "in 2 days")
      .replace(/\{emoji\}/gi, giveawayStyle.emoji || "🎉")
      .replace(
        /\{requirements\}/gi,
        "• Level 5+\n• 50+ messages (last 7 days)",
      );

  const saveSettings = async () => {
    const emptyStreamCreators = streamAlerts.creators.filter(
      (creator) => !String(creator.platformId || "").trim(),
    );
    if (emptyStreamCreators.length > 0) {
      showNotification(
        "Each tracked stream needs a Twitch login or YouTube handle before saving.",
        "error",
      );
      return;
    }

    setIsSaving(true);
    try {
      const hasStreamCreators = streamAlerts.creators.some((creator) =>
        String(creator.platformId || "").trim(),
      );
      const res = await fetch(`${API_BASE}/settings/${guildId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          welcomeMessage: welcomeMsg,
          welcomeChannel: selectedChannel,
          leaveMessage: leaveMsg,
          leaveChannel: selectedLeaveChannel,
          embedColor,
          welcomeTitle,
          showThumbnail,
          showTimestamp,
          welcomeMention,
          welcomeImage: welcomeImage || null,
          welcomePingEnabled,
          welcomePingChannelId: welcomePingEnabled
            ? welcomePingChannelId || null
            : null,
          welcomePingRoleId: welcomePingEnabled
            ? welcomePingRoleId || null
            : null,
          welcomePingMessage: welcomePingEnabled
            ? welcomePingMessage.trim() || null
            : null,
          leaveImage: leaveImage || null,
          memberRole,
          unverifiedRole,
          jailRole,
          autoRoleId: autoRoleId || null,
          leaveTitle,
          leaveColor,
          showLeaveThumbnail,
          showLeaveTimestamp,
          logLeaves,
          aiChannel: showAiChannel ? aiChannel : null,
          jailChannel,
          suggestChannel,
          botAnnouncementsChannel: botAnnouncementsChannel || null,
          birthdayChannel: birthdayChannel || null,
          countingChannel,
          inviteLogChannel: inviteLogChannel || null,
          quotesChannelId: quotesChannelId || null,
          ...(commandPrefixDirty
            ? { commandPrefix: commandPrefix || null }
            : {}),
          antiLinks,
          linkBlacklist,
          antiLinkExemptChannelIds,
          antiLinkExemptCategoryIds,
          antiSpamEnabled,
          antiSpamMaxMessages,
          antiSpamIntervalSeconds,
          antiSpamExemptChannelIds,
          antiSpamExemptCategoryIds,
          maxMentions,
          forbiddenWords,
          modLogChannel: logs.mod.channelId || modLogChannel,
          messageLogChannel: logs.message.channelId || messageLogChannel,
          reportLogChannel: logs.report.channelId || reportLogChannel,
          banDmEnabled,
          banAppealsEnabled,
          banAppealChannelId: banAppealsEnabled
            ? banAppealChannelId || null
            : null,
          banUnbanServerEnabled,
          banUnbanServerInvite: banUnbanServerEnabled
            ? banUnbanServerInvite.trim() || null
            : null,
          banUnbanServerMessage: banUnbanServerEnabled
            ? banUnbanServerMessage.trim() || null
            : null,
          logs,
          welcomeFooter,
          leaveFooter,
          growthChannel,
          growthMessage,
          serverStats,
          commandToggles,
          levelingEnabled,
          levelUpChannel: levelUpChannel || null,
          serverLevelingEnabled,
          serverLevelUpChannel: serverLevelUpChannel || null,
          levelRoles,
          globalMultiplier: showProgressionAdmin ? globalMultiplier : undefined,
          multiplierExpires: showProgressionAdmin
            ? multiplierExpires
            : undefined,
          boosterHours: showProgressionAdmin ? boosterHours : undefined,
          boosting: {
            customRolesEnabled: customBoosterRoles,
            boostRoleId: boostRoleId || null,
            boostChannel: boostChannel || null,
            boostMessage,
            customRoleAnchorId: customRoleAnchorId || null,
            customRolePlacement:
              customRolePlacement === "below" ? "below" : "above",
          },
          siteSupporterPerksEnabled,
          donorSupport: showProgressionAdmin
            ? {
                roleIds: {
                  supporter: donorSupport.roleIds?.supporter || null,
                  patron: donorSupport.roleIds?.patron || null,
                  benefactor: donorSupport.roleIds?.benefactor || null,
                },
                announceChannelId: donorSupport.announceChannelId || null,
                announceMessage: donorSupport.announceMessage,
              }
            : undefined,
          streamAlerts: {
            enabled:
              streamAlerts.enabled ||
              (Boolean(streamAlerts.channelId) && hasStreamCreators),
            channelId: streamAlerts.channelId || null,
            pingRoleId: streamAlerts.pingRoleId || null,
            mentionEveryone: streamAlerts.mentionEveryone,
            combineMultiPlatform: Boolean(streamAlerts.combineMultiPlatform),
            message: streamAlerts.message,
            creators: streamAlerts.creators.map((creator) => ({
              ...creator,
              platformId: String(creator.platformId || "").trim(),
              displayName: String(creator.displayName || "").trim(),
              discordUserId: String(creator.discordUserId || "").trim() || null,
            })),
          },
          qotd: {
            enabled: qotd.enabled,
            channelId: qotd.channelId || null,
            pingRoleId: qotd.pingRoleId || null,
            timezone: qotd.timezone || "America/Chicago",
            postHour: Number.isFinite(Number(qotd.postHour))
              ? Number(qotd.postHour)
              : 9,
            title: qotd.title || "❓ Question of the Day",
            source: qotd.source || "both",
            questions: (qotd.questions || [])
              .map((q) => String(q || "").trim())
              .filter(Boolean)
              .slice(0, 200),
          },
          confessions: {
            enabled: confessions.enabled,
            channelId: confessions.channelId || null,
            panelChannelId: confessions.panelChannelId || null,
            logChannelId: confessions.logChannelId || null,
            embedColor: confessions.embedColor || "#4a0e17",
            panelTitle: confessions.panelTitle,
            panelDescription: confessions.panelDescription,
            panelFooter: confessions.panelFooter,
            allowPolls: confessions.allowPolls,
            allowReplies: confessions.allowReplies,
            allowCustomColor: confessions.allowCustomColor,
            cooldownSeconds: Number(confessions.cooldownSeconds) || 0,
          },
          shopItems,
          rolePanels,
          colorPanels: {
            panelTitle: colorPanels.panelTitle || null,
            panelMessage: colorPanels.panelMessage || null,
          },
          partnershipConfig,
          partnershipBlacklist,
          staffQuota,
          security,
          falseGate: {
            enabled: Boolean(falseGate.enabled),
            channelId: falseGate.enabled ? falseGate.channelId || null : null,
            action: falseGate.action || "ban",
            timeoutMinutes: falseGate.timeoutMinutes ?? 60,
            deleteMessage: falseGate.deleteMessage !== false,
            ignoreStaff: falseGate.ignoreStaff !== false,
            reason:
              falseGate.reason?.trim() || "Spoke in the False Gate.",
          },
          tickets: {
            panelChannelId: ticketPanelChannelId || null,
            categoryId: ticketCategoryId || null,
            staffRoleId: ticketStaffRoleId || null,
            panelTitle: ticketPanelTitle,
            panelDescription: ticketPanelDescription,
            panelFooter: ticketPanelFooter,
            buttonLabel: ticketButtonLabel,
            welcomeTitle: ticketWelcomeTitle,
            welcomeDescription: ticketWelcomeDescription,
            pingStaffOnOpen: ticketPingStaffOnOpen,
            pingRoleId: ticketPingRoleId || null,
            closeRoleId: ticketCloseRoleId || null,
            transcriptChannelId: ticketTranscriptChannelId || null,
          },
          staffTasks: {
            channelId: staffTasks.channelId || null,
            creatorRoleIds: Array.isArray(staffTasks.creatorRoleIds)
              ? staffTasks.creatorRoleIds
              : [],
          },
          giveawayStyle,
          applications,
          verification: {
            mode: verificationMode,
            title: verificationTitle,
            description: verificationDescription,
            footer: verificationFooter,
            buttonLabel: verificationButtonLabel,
          },
        }),
      });

      if (res.ok) {
        const result = await res.json();
        if (result.data?.multiplierExpires) {
          setMultiplierExpires(result.data.multiplierExpires);
          setBoosterHours(0);
        }
        if (Array.isArray(result.data?.serverStats)) {
          setServerStats(result.data.serverStats);
        }
        if (result.data?.streamAlerts) {
          const saved = result.data.streamAlerts;
          setStreamAlerts({
            enabled: saved.enabled ?? false,
            channelId: saved.channelId || "",
            pingRoleId: saved.pingRoleId || "",
            mentionEveryone: saved.mentionEveryone ?? false,
            combineMultiPlatform: saved.combineMultiPlatform ?? false,
            message:
              saved.message ||
              "🔴 **{streamer}** is live on **{platform}**!\n{title}\n{url}",
            creators: (saved.creators || []).map((creator) => ({
              id:
                creator.id ||
                creator._id?.toString?.() ||
                crypto.randomUUID(),
              platform: creator.platform || "twitch",
              platformId: creator.platformId || "",
              displayName: creator.displayName || "",
              discordUserId: creator.discordUserId || "",
            })),
          });
        }
        if (result.data?.qotd) {
          const saved = result.data.qotd;
          setQotd({
            enabled: saved.enabled ?? false,
            channelId: saved.channelId || "",
            pingRoleId: saved.pingRoleId || "",
            timezone: saved.timezone || "America/Chicago",
            postHour: saved.postHour ?? 9,
            title: saved.title || "❓ Question of the Day",
            source: saved.source || "both",
            questions: Array.isArray(saved.questions)
              ? saved.questions.filter(Boolean)
              : [],
          });
        }
        if (result.data?.confessions) {
          const saved = result.data.confessions;
          setConfessions({
            enabled: saved.enabled ?? false,
            channelId: saved.channelId || "",
            panelChannelId: saved.panelChannelId || "",
            logChannelId: saved.logChannelId || "",
            embedColor: saved.embedColor || "#4a0e17",
            panelTitle: saved.panelTitle || "Anonymous Confessions",
            panelDescription:
              saved.panelDescription ||
              "Share something anonymously with the server.",
            panelFooter: saved.panelFooter || "Kami · Confessions",
            allowPolls: saved.allowPolls !== false,
            allowReplies: saved.allowReplies !== false,
            allowCustomColor: saved.allowCustomColor !== false,
            cooldownSeconds: saved.cooldownSeconds ?? 60,
          });
        }
        if (result?.streamAlertsWarning) {
          showNotification(result.streamAlertsWarning, "error");
        }
        if (result?.serverStatUpdate?.counts) {
          setStatCounts(result.serverStatUpdate.counts);
        }
        const updatedNames = result?.serverStatUpdate?.updated || [];
        if (updatedNames.length > 0) {
          // Only reflect names Discord actually accepted — not rate-limit queues.
          const nameByChannel = Object.fromEntries(
            updatedNames
              .filter(
                (u) =>
                  u.channelId &&
                  u.name &&
                  !u.error &&
                  !u.queued &&
                  (u.applied || !u.skipped),
              )
              .map((u) => [u.channelId, u.name]),
          );
          if (Object.keys(nameByChannel).length > 0) {
            setChannels((prev) =>
              prev.map((ch) =>
                nameByChannel[ch.id]
                  ? { ...ch, name: nameByChannel[ch.id] }
                  : ch,
              ),
            );
          }
        }
        const appliedCount = updatedNames.filter(
          (u) => u.applied || (!u.skipped && !u.error),
        ).length;
        const queuedCount = updatedNames.filter((u) => u.queued).length;
        const failedCount = updatedNames.filter((u) => u.error).length;
        if (updatedNames.length > 0) {
          const parts = [
            `Settings saved. Renamed ${appliedCount}/${updatedNames.length} stat channel(s)`,
          ];
          if (queuedCount > 0) {
            parts.push(
              `${queuedCount} queued (Discord limits ~2 renames / 10 min — Kami retries automatically)`,
            );
          }
          if (failedCount > 0) {
            const firstErr = updatedNames.find((u) => u.error)?.error;
            parts.push(
              `${failedCount} failed${firstErr ? `: ${String(firstErr).slice(0, 80)}` : ""}`,
            );
          }
          showNotification(
            `${parts.join(". ")}. ☾`,
            failedCount > 0 ? "error" : "success",
          );
        } else {
          showNotification("Settings updated. ☾");
        }
      } else throw new Error();
      setHasUnsavedChanges(false);
      setCommandPrefixDirty(false);
    } catch (err) {
      showNotification("Failed to save settings.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={`container settings-page${
        hasUnsavedChanges ? " settings-page--unsaved" : ""
      }`}
    >
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          {toast.message}
        </div>
      )}

      <Link to="/servers" className="back-link settings-back">
        ← Back to servers
      </Link>

      <header className="settings-header">
        <img
          src={
            guild?.icon
              ? `https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`
              : "https://cdn.discordapp.com/embed/avatars/0.png"
          }
          alt=""
          className="settings-server-icon"
        />
        <div className="settings-header-text">
          <h1>{guild?.name || "Server"} Settings</h1>
          <div className="settings-meta">
            <span>{stats.memberCount} members</span>
            <span>·</span>
            <span>Owner: {stats.ownerName}</span>
            <span>·</span>
            <span className="status-pill">Bot active</span>
          </div>
        </div>
      </header>

      <details className="formatting-hint formatting-hint-collapsible">
        <summary>Variables &amp; markdown for messages</summary>
        <div className="formatting-hint-body">
          <span>Variables:</span>
          <code>{"{user}"}</code>
          <code>{"{userName}"}</code>
          <code>{"{server}"}</code>
          <code>{"{server.boosts}"}</code>
          <code>{"{server.boost_level}"}</code>
          <code>{"{count}"}</code>
          <code>{"<#channel_id>"}</code>
          <span className="hint-divider">Markdown:</span>
          <code>{"**bold**"}</code>
          <code>{"*italic*"}</code>
          <code>{"> quote"}</code>
        </div>
      </details>

      <SettingsSearchProvider>
        <div className="settings-shell">
          <SettingsSearchBar />
          <SettingsSearchSync
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            showProgressionAdmin={showProgressionAdmin}
            skipAutoJump={isMobileSettings && mobileShowIndex}
          />
          <div
            className={`settings-shell-body${
              isMobileSettings && mobileShowIndex
                ? " settings-shell-body--mobile-index"
                : ""
            }${
              isMobileSettings && !mobileShowIndex
                ? " settings-shell-body--mobile-detail"
                : ""
            }`}
          >
            <SettingsSidebar
              activeSection={activeSection}
              onSelect={handleSelectSection}
              showProgressionAdmin={showProgressionAdmin}
              mobileCatalog={isMobileSettings && mobileShowIndex}
            />
            <div className="settings-main-column">
              {activeNavItem ? (
                <header className="settings-main-header">
                  {isMobileSettings ? (
                    <button
                      type="button"
                      className="settings-mobile-back"
                      onClick={handleMobileBackToIndex}
                    >
                      ← All settings
                    </button>
                  ) : null}
                  <p className="settings-main-eyebrow">
                    {activeNavItem.groupLabel}
                  </p>
                  <h2 className="settings-main-title">{activeNavItem.title}</h2>
                </header>
              ) : null}
              <div
                ref={settingsFormRef}
                className="settings-main-panel"
              onChange={(event) => {
                if (isSaving) return;
                const target = event.target;
                if (target?.classList?.contains("channel-picker-search")) return;
                if (target?.matches?.("input, textarea, select")) {
                  setHasUnsavedChanges(true);
                }
              }}
            >
{activeSection === "kamiProfile" && (
<KamiProfileSection
                  guildId={guildId}
                  apiBase={API_BASE}
                  token={dashboardToken}
                />
)}
{activeSection === "serverHost" && (
<HostSection
                  guildId={guildId}
                  apiBase={API_BASE}
                  token={dashboardToken}
                  channels={channels}
                  roles={roles}
                />
)}
{activeSection === "welcome" && (
<WelcomeSection
                channels={channels}
                roles={roles}
                selectedChannel={selectedChannel}
                setSelectedChannel={setSelectedChannel}
                embedColor={embedColor}
                setEmbedColor={setEmbedColor}
                welcomeTitle={welcomeTitle}
                setWelcomeTitle={setWelcomeTitle}
                welcomeMsg={welcomeMsg}
                setWelcomeMsg={setWelcomeMsg}
                welcomeFooter={welcomeFooter}
                setWelcomeFooter={setWelcomeFooter}
                showThumbnail={showThumbnail}
                setShowThumbnail={setShowThumbnail}
                showTimestamp={showTimestamp}
                setShowTimestamp={setShowTimestamp}
                welcomeMention={welcomeMention}
                setWelcomeMention={setWelcomeMention}
                welcomeImage={welcomeImage}
                setWelcomeImage={setWelcomeImage}
                welcomePingEnabled={welcomePingEnabled}
                setWelcomePingEnabled={setWelcomePingEnabled}
                welcomePingChannelId={welcomePingChannelId}
                setWelcomePingChannelId={setWelcomePingChannelId}
                welcomePingRoleId={welcomePingRoleId}
                setWelcomePingRoleId={setWelcomePingRoleId}
                welcomePingMessage={welcomePingMessage}
                setWelcomePingMessage={setWelcomePingMessage}
                guildId={guildId}
                apiBase={API_BASE}
                imageUploading={embedImageUploading}
                setImageUploading={setEmbedImageUploading}
                isOpen={isWelcomeOpen}
                setIsOpen={setIsWelcomeOpen}
              />
)}
              {activeSection === "leave" && (
<LeaveSection
                channels={channels}
                selectedLeaveChannel={selectedLeaveChannel}
                setSelectedLeaveChannel={setSelectedLeaveChannel}
                leaveColor={leaveColor}
                setLeaveColor={setLeaveColor}
                leaveTitle={leaveTitle}
                setLeaveTitle={setLeaveTitle}
                leaveMsg={leaveMsg}
                setLeaveMsg={setLeaveMsg}
                leaveFooter={leaveFooter}
                setLeaveFooter={setLeaveFooter}
                showLeaveThumbnail={showLeaveThumbnail}
                setShowLeaveThumbnail={setShowLeaveThumbnail}
                showLeaveTimestamp={showLeaveTimestamp}
                setShowLeaveTimestamp={setShowLeaveTimestamp}
                leaveImage={leaveImage}
                setLeaveImage={setLeaveImage}
                logLeaves={logLeaves}
                setLogLeaves={setLogLeaves}
                guildId={guildId}
                apiBase={API_BASE}
                imageUploading={embedImageUploading}
                setImageUploading={setEmbedImageUploading}
                isOpen={isLeaveOpen}
                setIsOpen={setIsLeaveOpen}
              />
)}
              {activeSection === "roles" && (
<RolesSection
                roles={roles}
                memberRole={memberRole}
                setMemberRole={setMemberRole}
                unverifiedRole={unverifiedRole}
                setUnverifiedRole={setUnverifiedRole}
                jailRole={jailRole}
                setJailRole={setJailRole}
                autoRoleId={autoRoleId}
                setAutoRoleId={setAutoRoleId}
              />
)}
              {activeSection === "verification" && (
<VerificationSection
                verificationMode={verificationMode}
                setVerificationMode={setVerificationMode}
                verificationTitle={verificationTitle}
                setVerificationTitle={setVerificationTitle}
                verificationDescription={verificationDescription}
                setVerificationDescription={setVerificationDescription}
                verificationFooter={verificationFooter}
                setVerificationFooter={setVerificationFooter}
                verificationButtonLabel={verificationButtonLabel}
                setVerificationButtonLabel={setVerificationButtonLabel}
                isOpen={isVerificationOpen}
                setIsOpen={setIsVerificationOpen}
              />
)}
{activeSection === "channels" && (
<ChannelsSection
                channels={channels}
                showAiChannel={showAiChannel}
                aiChannel={aiChannel}
                setAiChannel={setAiChannel}
                reportLogChannel={reportLogChannel}
                setReportLogChannel={(id) => {
                  setReportLogChannel(id);
                  setLogs((prev) => ({
                    ...prev,
                    report: { ...prev.report, channelId: id },
                  }));
                }}
                jailChannel={jailChannel}
                setJailChannel={setJailChannel}
                suggestChannel={suggestChannel}
                setSuggestChannel={setSuggestChannel}
                botAnnouncementsChannel={botAnnouncementsChannel}
                setBotAnnouncementsChannel={setBotAnnouncementsChannel}
                birthdayChannel={birthdayChannel}
                setBirthdayChannel={setBirthdayChannel}
                countingChannel={countingChannel}
                setCountingChannel={setCountingChannel}
                inviteLogChannel={inviteLogChannel}
                setInviteLogChannel={setInviteLogChannel}
                quotesChannelId={quotesChannelId}
                setQuotesChannelId={setQuotesChannelId}
                commandPrefix={commandPrefix}
                setCommandPrefix={(value) => {
                  setCommandPrefix(value);
                  setCommandPrefixDirty(true);
                }}
              />
)}
              {activeSection === "serverStats" && (
<SpiritCounterSection
                channels={channels}
                roles={roles}
                serverStats={serverStats}
                setServerStats={setServerStats}
                isOpen={isGrowthOpen}
                setIsOpen={() => setGrowthOpen(!isGrowthOpen)}
              />
)}
{activeSection === "tickets" && (
<TicketsSection
                channels={channels}
                roles={roles}
                ticketPanelChannelId={ticketPanelChannelId}
                setTicketPanelChannelId={setTicketPanelChannelId}
                ticketCategoryId={ticketCategoryId}
                setTicketCategoryId={setTicketCategoryId}
                ticketStaffRoleId={ticketStaffRoleId}
                setTicketStaffRoleId={setTicketStaffRoleId}
                ticketPanelTitle={ticketPanelTitle}
                setTicketPanelTitle={setTicketPanelTitle}
                ticketPanelDescription={ticketPanelDescription}
                setTicketPanelDescription={setTicketPanelDescription}
                ticketPanelFooter={ticketPanelFooter}
                setTicketPanelFooter={setTicketPanelFooter}
                ticketButtonLabel={ticketButtonLabel}
                setTicketButtonLabel={setTicketButtonLabel}
                ticketWelcomeTitle={ticketWelcomeTitle}
                setTicketWelcomeTitle={setTicketWelcomeTitle}
                ticketWelcomeDescription={ticketWelcomeDescription}
                setTicketWelcomeDescription={setTicketWelcomeDescription}
                ticketPingStaffOnOpen={ticketPingStaffOnOpen}
                setTicketPingStaffOnOpen={setTicketPingStaffOnOpen}
                ticketPingRoleId={ticketPingRoleId}
                setTicketPingRoleId={setTicketPingRoleId}
                ticketCloseRoleId={ticketCloseRoleId}
                setTicketCloseRoleId={setTicketCloseRoleId}
                ticketTranscriptChannelId={ticketTranscriptChannelId}
                setTicketTranscriptChannelId={setTicketTranscriptChannelId}
                isOpen={isTicketsOpen}
                setIsOpen={setIsTicketsOpen}
              />
)}
              {activeSection === "staffTasks" && (
<StaffTasksSection
                channels={channels}
                roles={roles}
                staffTasks={staffTasks}
                setStaffTasks={setStaffTasks}
                isOpen={isStaffTasksOpen}
                setIsOpen={setIsStaffTasksOpen}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
)}
              {activeSection === "guardian" && (
<GuardianSection
                channels={channels}
                logs={logs}
                setLogs={setLogs}
                modLogChannel={modLogChannel}
                setModLogChannel={setModLogChannel}
                messageLogChannel={messageLogChannel}
                setMessageLogChannel={setMessageLogChannel}
                commandToggles={commandToggles}
                onToggleCommand={handleToggleCommand}
                setCommandToggles={setCommandToggles}
                banDmEnabled={banDmEnabled}
                setBanDmEnabled={setBanDmEnabled}
                banAppealsEnabled={banAppealsEnabled}
                setBanAppealsEnabled={setBanAppealsEnabled}
                banAppealChannelId={banAppealChannelId}
                setBanAppealChannelId={setBanAppealChannelId}
                banUnbanServerEnabled={banUnbanServerEnabled}
                setBanUnbanServerEnabled={setBanUnbanServerEnabled}
                banUnbanServerInvite={banUnbanServerInvite}
                setBanUnbanServerInvite={setBanUnbanServerInvite}
                banUnbanServerMessage={banUnbanServerMessage}
                setBanUnbanServerMessage={setBanUnbanServerMessage}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
)}
              {activeSection === "automod" && (
<AutoModSection
                channels={channels}
                forbiddenWords={forbiddenWords}
                setForbiddenWords={setForbiddenWords}
                newWord={newWord}
                setNewWord={setNewWord}
                antiLinks={antiLinks}
                setAntiLinks={setAntiLinks}
                linkBlacklist={linkBlacklist}
                setLinkBlacklist={setLinkBlacklist}
                antiLinkExemptChannelIds={antiLinkExemptChannelIds}
                setAntiLinkExemptChannelIds={setAntiLinkExemptChannelIds}
                antiLinkExemptCategoryIds={antiLinkExemptCategoryIds}
                setAntiLinkExemptCategoryIds={setAntiLinkExemptCategoryIds}
                antiSpamEnabled={antiSpamEnabled}
                setAntiSpamEnabled={setAntiSpamEnabled}
                antiSpamMaxMessages={antiSpamMaxMessages}
                setAntiSpamMaxMessages={setAntiSpamMaxMessages}
                antiSpamIntervalSeconds={antiSpamIntervalSeconds}
                setAntiSpamIntervalSeconds={setAntiSpamIntervalSeconds}
                antiSpamExemptChannelIds={antiSpamExemptChannelIds}
                setAntiSpamExemptChannelIds={setAntiSpamExemptChannelIds}
                antiSpamExemptCategoryIds={antiSpamExemptCategoryIds}
                setAntiSpamExemptCategoryIds={setAntiSpamExemptCategoryIds}
                newLink={newLink}
                setNewLink={setNewLink}
                maxMentions={maxMentions}
                setMaxMentions={setMaxMentions}
                commandToggles={commandToggles}
                setCommandToggles={setCommandToggles}
                security={security}
                setSecurity={setSecurity}
                falseGate={falseGate}
                setFalseGate={setFalseGate}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
)}
              {activeSection === "staffQuota" && (
<StaffQuotaSection
                channels={channels}
                roles={roles}
                staffQuota={staffQuota}
                setStaffQuota={setStaffQuota}
              />
)}
{activeSection === "leveling" && (
<LevelingSection
                  channels={channels}
                  levelingEnabled={levelingEnabled}
                  setLevelingEnabled={setLevelingEnabled}
                  levelUpChannel={levelUpChannel}
                  setLevelUpChannel={setLevelUpChannel}
                  globalMultiplier={globalMultiplier}
                  setGlobalMultiplier={setGlobalMultiplier}
                  boosterHours={boosterHours}
                  setBoosterHours={setBoosterHours}
                  multiplierExpires={multiplierExpires}
                  progressionAdmin={showProgressionAdmin}
                />
)}
              {activeSection === "serverLeveling" && (
<ServerLevelingSection
                channels={channels}
                roles={roles}
                serverLevelingEnabled={serverLevelingEnabled}
                setServerLevelingEnabled={setServerLevelingEnabled}
                serverLevelUpChannel={serverLevelUpChannel}
                setServerLevelUpChannel={setServerLevelUpChannel}
                levelRoles={levelRoles}
                setLevelRoles={setLevelRoles}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
)}
              {activeSection === "boosting" && (
<BoostingSection
                channels={channels}
                roles={roles}
                customBoosterRoles={customBoosterRoles}
                setCustomBoosterRoles={setCustomBoosterRoles}
                siteSupporterPerksEnabled={siteSupporterPerksEnabled}
                setSiteSupporterPerksEnabled={(v) => {
                  setSiteSupporterPerksEnabled(v);
                  setHasUnsavedChanges(true);
                }}
                boostRoleId={boostRoleId}
                setBoostRoleId={setBoostRoleId}
                boostChannel={boostChannel}
                setBoostChannel={setBoostChannel}
                boostMessage={boostMessage}
                setBoostMessage={setBoostMessage}
                customRoleAnchorId={customRoleAnchorId}
                setCustomRoleAnchorId={(v) => {
                  setCustomRoleAnchorId(v);
                  setHasUnsavedChanges(true);
                }}
                customRolePlacement={customRolePlacement}
                setCustomRolePlacement={(v) => {
                  setCustomRolePlacement(v);
                  setHasUnsavedChanges(true);
                }}
              />
)}
              {activeSection === "donorSupport" && showProgressionAdmin && (
<DonorSupportSection
                  channels={channels}
                  roles={roles}
                  donorSupport={donorSupport}
                  setDonorSupport={setDonorSupport}
                />
)}
{activeSection === "giveaways" && (
<GiveawaySection
                giveawayStyle={giveawayStyle}
                setGiveawayStyle={setGiveawayStyle}
                embedColor={embedColor}
                isOpen={isGiveawayOpen}
                setIsOpen={setIsGiveawayOpen}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
)}
              {activeSection === "applications" && (
<ApplicationsSection
                channels={channels}
                roles={roles}
                applications={applications}
                setApplications={setApplications}
                isOpen={isApplicationsOpen}
                setIsOpen={setIsApplicationsOpen}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
)}
              {activeSection === "streamAlerts" && (
<StreamAlertsSection
                channels={channels}
                roles={roles}
                streamAlerts={streamAlerts}
                setStreamAlerts={setStreamAlerts}
              />
)}
              {activeSection === "qotd" && (
<QotdSection
                channels={channels}
                roles={roles}
                qotd={qotd}
                setQotd={setQotd}
              />
)}
              {activeSection === "confessions" && (
<ConfessionsSection
                channels={channels}
                confessions={confessions}
                setConfessions={setConfessions}
              />
)}
              {activeSection === "shop" && (
<ShopSection
                roles={roles}
                shopItems={shopItems}
                setShopItems={setShopItems}
              />
)}
              {activeSection === "partnerships" && (
<PartnershipSection
                channels={channels}
                roles={roles}
                partnershipConfig={partnershipConfig}
                setPartnershipConfig={setPartnershipConfig}
                partnershipBlacklist={partnershipBlacklist}
                setPartnershipBlacklist={setPartnershipBlacklist}
                newBlacklistId={newBlacklistId}
                setNewBlacklistId={setNewBlacklistId}
              />
)}
              {activeSection === "rolePanels" && (
<RolePanelsSection
                channels={channels}
                rolePanels={rolePanels}
                setRolePanels={setRolePanels}
              />
)}
              {activeSection === "colorPanels" && (
<ColorPanelsSection
                colorPanels={colorPanels}
                setColorPanels={setColorPanels}
              />
)}
              {!activeNavItem && (
                <p className="settings-main-empty">
                  No sections match your search. Clear the search box or try
                  different keywords.
                </p>
              )}
            </div>
          </div>

          <div className="preview-column">
          <div className="preview-card">
            <span className="preview-label">Live preview</span>

            {!hasPreview && (
              <p className="preview-empty">
                Select Welcome, Leave, Verification, Server Stats, Tickets, or
                Giveaways to see a live preview here.
              </p>
            )}

            {/* --- 1. WELCOME PREVIEW --- */}
            {activeSection === "welcome" && (
              <DiscordEmbedPreview
                color={embedColor}
                title={welcomeTitle || "☾ Nocturne"}
                descriptionHtml={formatPreviewText(
                  welcomeMsg || "Welcome to Nocturne, {user}!",
                )}
                thumbnailUrl={userAvatar}
                showThumbnail={showThumbnail}
                imageUrl={welcomeImage || null}
                footerHtml={
                  welcomeFooter
                    ? formatPreviewText(welcomeFooter)
                    : null
                }
                showTimestamp={showTimestamp}
                mention={
                  welcomeMention
                    ? `@${user?.username || "New Member"}`
                    : null
                }
              />
            )}

            {/* --- 2. LEAVE PREVIEW --- */}
            {activeSection === "leave" && (
              <DiscordEmbedPreview
                color={leaveColor}
                title={leaveTitle || "✦ Farewell"}
                descriptionHtml={formatPreviewText(
                  leaveMsg || "{user} has slipped back into the dark.",
                )}
                thumbnailUrl={userAvatar}
                showThumbnail={showLeaveThumbnail}
                imageUrl={leaveImage || null}
                footerHtml={
                  leaveFooter ? formatPreviewText(leaveFooter) : null
                }
                showTimestamp={showLeaveTimestamp}
                className="preview-leave"
              />
            )}

            {/* --- 3. SERVER STATS PREVIEW --- */}
            {activeSection === "serverStats" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {(serverStats.length
                  ? serverStats
                  : [
                      {
                        id: "preview",
                        type: "all",
                        format: "👥 Members: {count}",
                      },
                    ]
                ).map((stat) => (
                  <div
                    key={stat.id}
                    className="voice-channel-preview"
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      padding: "10px",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "#b9bbbe",
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      style={{ opacity: 0.6 }}
                    >
                      <path d="M11.383 3.07904C11.009 2.92504 10.579 3.01004 10.293 3.29604L6 8.00004H3C2.447 8.00004 2 8.44704 2 9.00004V15C2 15.553 2.447 16 3 16H6L10.293 20.704C10.441 20.852 10.637 20.933 10.841 20.933C11.026 20.933 11.21 20.871 11.366 20.747C11.758 20.435 12 19.954 12 19.438V4.56204C12 4.04604 11.758 3.56504 11.383 3.07904Z" />
                      <path d="M15 12C15 9.356 13.435 7.076 11 6V18C13.435 16.924 15 14.644 15 12Z" />
                    </svg>
                    <span style={{ fontSize: "0.95rem", fontWeight: "500" }}>
                      {(stat.format || "Count: {count}").replace(
                        "{count}",
                        String(
                          statCounts[stat.id] ??
                            (stat.type === "all" || stat.type === "humans"
                              ? stats.memberCount
                              : "0"),
                        ),
                      )}
                      <span style={{ opacity: 0.55, marginLeft: 8 }}>
                        ({stat.type}
                        {stat.type === "role" && stat.roleId
                          ? ` · ${
                              roles.find((r) => r.id === stat.roleId)?.name ||
                              "role"
                            }`
                          : ""}
                        )
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* --- 4. VERIFICATION PREVIEW --- */}
            {activeSection === "verification" && (
              <div className="discord-preview-stack">
                <span className="preview-sublabel">Verification panel</span>
                <div
                  className="discord-mockup"
                  style={{ borderLeftColor: embedColor }}
                >
                  <div className="discord-author">
                    <div className="author-content">
                      <div className="embed-title">
                        {verificationTitle || "Nocturne Verification"}
                      </div>
                      <div
                        className="embed-description"
                        dangerouslySetInnerHTML={{
                          __html: formatPreviewText(
                            verificationDescription ||
                              "By clicking the button below, you agree to the server rules.",
                          ),
                        }}
                      />
                    </div>
                  </div>
                  {verificationFooter && (
                    <div className="discord-footer">
                      <span
                        className="footer-text"
                        dangerouslySetInnerHTML={{
                          __html: formatPreviewText(verificationFooter),
                        }}
                      />
                    </div>
                  )}
                  <div className="discord-button-row">
                    <button type="button" className="discord-button-success">
                      {verificationButtonLabel || "Complete Verification"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* --- 5. SUPPORT TICKETS PREVIEW --- */}
            {activeSection === "tickets" && (
              <div className="discord-preview-stack">
                <span className="preview-sublabel">Panel message</span>
                <div
                  className="discord-mockup"
                  style={{ borderLeftColor: embedColor }}
                >
                  <div className="discord-author">
                    <div className="author-content">
                      <div className="embed-title">
                        {ticketPanelTitle || "☾ Support Desk"}
                      </div>
                      <div
                        className="embed-description"
                        dangerouslySetInnerHTML={{
                          __html: formatPreviewText(
                            ticketPanelDescription ||
                              "Need help? Open a private channel and a Warden will meet you here.",
                          ),
                        }}
                      />
                    </div>
                  </div>
                  {ticketPanelFooter && (
                    <div className="discord-footer">
                      <span
                        className="footer-text"
                        dangerouslySetInnerHTML={{
                          __html: formatPreviewText(ticketPanelFooter),
                        }}
                      />
                    </div>
                  )}
                  <div className="discord-button-row">
                    <button type="button" className="discord-button-secondary">
                      🎫 {ticketButtonLabel || "Open Support"}
                    </button>
                  </div>
                </div>

                <span className="preview-sublabel">Inside a new ticket</span>
                <div
                  className="discord-mockup"
                  style={{ borderLeftColor: embedColor }}
                >
                  <div className="discord-author">
                    <div className="author-content">
                      <div className="embed-title">
                        {ticketWelcomeTitle || "✦ Support Request"}
                      </div>
                      <div
                        className="embed-description"
                        dangerouslySetInnerHTML={{
                          __html: formatPreviewText(
                            ticketWelcomeDescription ||
                              "Welcome, {user}. A Warden will be with you shortly.",
                          ),
                        }}
                      />
                    </div>
                  </div>
                  <div className="discord-button-row">
                    <button type="button" className="discord-button-primary">
                      Claim
                    </button>
                    <button type="button" className="discord-button-danger">
                      🔒 Close
                    </button>
                    <button type="button" className="discord-button-secondary">
                      📝 Close with Reason
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeSection === "giveaways" && (
              <div className="discord-preview-stack">
                <span className="preview-sublabel">Active giveaway</span>
                <DiscordEmbedPreview
                  color={giveawayStyle.color || embedColor}
                  title={fillGiveawayPlain(giveawayStyle.title)}
                  descriptionHtml={formatPreviewText(giveawayStyle.description)}
                  footerHtml={
                    giveawayStyle.footer
                      ? formatPreviewText(giveawayStyle.footer)
                      : null
                  }
                />
                <div className="discord-reaction">
                  <span>{giveawayStyle.emoji || "🎉"}</span>
                  <span>5</span>
                </div>

                <span className="preview-sublabel">When it ends</span>
                <DiscordEmbedPreview
                  color={giveawayStyle.endedColor || "#2b2d31"}
                  title={fillGiveawayPlain(giveawayStyle.endedTitle)}
                  descriptionHtml={formatPreviewText(
                    giveawayStyle.endedDescription,
                  )}
                />
                <div
                  className="discord-content-ping"
                  dangerouslySetInnerHTML={{
                    __html: formatPreviewText(giveawayStyle.winMessage),
                  }}
                />
              </div>
            )}
          </div>
        </div>
        </div>
        </div>
      </SettingsSearchProvider>

      {hasUnsavedChanges && (
        <div className="settings-footer">
          <button
            onClick={saveSettings}
            className="manage-btn save-fixed"
            disabled={isSaving}
          >
            {isSaving ? "SAVING..." : "SAVE ALL SETTINGS"}
          </button>
        </div>
      )}
    </div>
  );
}
